import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Search,
  Receipt as ReceiptIcon,
  DollarSign,
  Calendar,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import PaymentCard from "@/components/payments/PaymentCard";
import PaymentStats from "@/components/payments/PaymentStats";
import PaymentCalendar from "@/components/sales/PaymentCalendar";
import PaymentTracker from "@/components/sales/PaymentTracker";
import { ExportMenu } from "@/components/shared/ExportMenu";
import { useSalesAndPayments } from "@/context/SalesAndPaymentsContext";

export default function PaymentsTab() {
  const ctx = useSalesAndPayments();
  const {
  canEdit,
  activeSales,
  clients,
  apartments,
  filteredPayments,
  payments,
  receipts,
  totalPaymentAmount,
  avgPaymentAmount,
  delayedPaymentCount,
  paymentViewTab,
  setPaymentViewTab,
  paymentsSearchQuery,
  setPaymentsSearchQuery,
  paymentMethodFilter,
  setPaymentMethodFilter,
  paymentStatusFilter,
  setPaymentStatusFilter,
  paymentsDateRange,
  setPaymentsDateRange,
  isPaymentsFilterOpen,
  setIsPaymentsFilterOpen,
  setPaymentsQuickDate,
  getPaymentsFilterLabel,
  paymentsExportData,
  paymentsExportColumns,
  loadingPayments,
  setEditingPayment,
  setShowPaymentForm,
  handleDeletePayment,
  getPaymentDetails,
  getPaymentOverdueStatus,
  checkOverduePayments,
  calculateRemainingBalance,
} = ctx;

  return (
    <div className="space-y-6">
      <PaymentStats
        totalAmount={totalPaymentAmount}
        avgAmount={avgPaymentAmount}
        delayedCount={delayedPaymentCount}
        receipts={receipts}
        payments={payments}
      />
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex gap-2 bg-slate-100 p-1 rounded-lg">
          <Button
            variant={paymentViewTab === "list" ? "default" : "ghost"}
            size="sm"
            onClick={() => setPaymentViewTab("list")}
            className="gap-2"
          >
            <ReceiptIcon className="w-4 h-4" /> List
          </Button>
          <Button
            variant={paymentViewTab === "calendar" ? "default" : "ghost"}
            size="sm"
            onClick={() => setPaymentViewTab("calendar")}
            className="gap-2"
          >
            <Calendar className="w-4 h-4" /> Calendar
          </Button>
          <Button
            variant={paymentViewTab === "tracker" ? "default" : "ghost"}
            size="sm"
            onClick={() => setPaymentViewTab("tracker")}
            className="gap-2"
          >
            <DollarSign className="w-4 h-4" /> Tracker
          </Button>
        </div>
        <div className="flex gap-3 flex-wrap items-center">
          <Popover
            open={isPaymentsFilterOpen}
            onOpenChange={setIsPaymentsFilterOpen}
          >
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Calendar className="w-4 h-4" />
                {getPaymentsFilterLabel()}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-slate-500 uppercase">
                    Quick Filters
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant={
                        paymentsDateRange.type === "all" ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => setPaymentsQuickDate("all")}
                      className="text-sm"
                    >
                      All Time
                    </Button>
                    <Button
                      variant={
                        paymentsDateRange.type === "thisWeek"
                          ? "default"
                          : "outline"
                      }
                      size="sm"
                      onClick={() => setPaymentsQuickDate("thisWeek")}
                      className="text-sm"
                    >
                      This Week
                    </Button>
                    <Button
                      variant={
                        paymentsDateRange.type === "lastWeek"
                          ? "default"
                          : "outline"
                      }
                      size="sm"
                      onClick={() => setPaymentsQuickDate("lastWeek")}
                      className="text-sm"
                    >
                      Last Week
                    </Button>
                    <Button
                      variant={
                        paymentsDateRange.type === "thisMonth"
                          ? "default"
                          : "outline"
                      }
                      size="sm"
                      onClick={() => setPaymentsQuickDate("thisMonth")}
                      className="text-sm"
                    >
                      This Month
                    </Button>
                    <Button
                      variant={
                        paymentsDateRange.type === "lastMonth"
                          ? "default"
                          : "outline"
                      }
                      size="sm"
                      onClick={() => setPaymentsQuickDate("lastMonth")}
                      className="text-sm"
                    >
                      Last Month
                    </Button>
                    <Button
                      variant={
                        paymentsDateRange.type === "custom" ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() =>
                        setPaymentsDateRange({
                          ...paymentsDateRange,
                          type: "custom",
                        })
                      }
                      className="text-sm"
                    >
                      Custom
                    </Button>
                  </div>
                </div>
                {paymentsDateRange.type === "custom" && (
                  <div className="space-y-3 pt-3 border-t">
                    <div className="space-y-2">
                      <Label htmlFor="paymentsStartDate">Start Date</Label>
                      <Input
                        id="paymentsStartDate"
                        type="date"
                        value={paymentsDateRange.startDate}
                        onChange={(e) =>
                          setPaymentsDateRange({
                            ...paymentsDateRange,
                            startDate: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="paymentsEndDate">End Date</Label>
                      <Input
                        id="paymentsEndDate"
                        type="date"
                        value={paymentsDateRange.endDate}
                        onChange={(e) =>
                          setPaymentsDateRange({
                            ...paymentsDateRange,
                            endDate: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
          <ExportMenu
            data={paymentsExportData}
            columns={paymentsExportColumns}
            title="Payments List"
            filename={`payments_${new Date().toISOString().split("T")[0]}`}
          />
          {canEdit && (
            <Button
              onClick={() => {
                setEditingPayment(null);
                setShowPaymentForm(true);
              }}
              className="gap-2 bg-gradient-to-r from-slate-900 to-slate-700 hover:from-slate-800 hover:to-slate-600"
            >
              <Plus className="w-4 h-4" />
              Record Payment
            </Button>
          )}
        </div>
      </div>

      {/* Payment Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by client, unit, or reference..."
            className="pl-10"
            value={paymentsSearchQuery}
            onChange={(e) => setPaymentsSearchQuery(e.target.value)}
          />
        </div>
        <Select
          value={paymentMethodFilter}
          onValueChange={setPaymentMethodFilter}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Methods</SelectItem>
            <SelectItem value="Cash">Cash</SelectItem>
            <SelectItem value="Installment">Installment</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={paymentStatusFilter}
          onValueChange={setPaymentStatusFilter}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="on_time">On Time</SelectItem>
            <SelectItem value="delayed">Overdue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Payment Views */}
      {paymentViewTab === "list" && (
        <div className="space-y-4">
          <div className="grid gap-4 grid-cols-1">
            {loadingPayments ? (
              <div className="col-span-full text-center py-10 text-slate-500">
                Loading payments...
              </div>
            ) : filteredPayments.length === 0 ? (
              <div className="col-span-full text-center py-10 text-slate-500">
                No payments found.
              </div>
            ) : (
              filteredPayments.map((payment) => {
                const details = getPaymentDetails(payment);
                const delayInfo = getPaymentOverdueStatus(details.sale);
                return (
                  <PaymentCard
                    key={payment.id}
                    payment={payment}
                    {...details}
                    delayInfo={delayInfo}
                    onEdit={(p) => {
                      setEditingPayment(p);
                      setShowPaymentForm(true);
                    }}
                    onDelete={handleDeletePayment}
                  />
                );
              })
            )}
          </div>
        </div>
      )}
      {paymentViewTab === "calendar" && (
        <PaymentCalendar
          sales={activeSales}
          clients={clients}
          apartments={apartments}
          checkOverduePayments={checkOverduePayments}
          // Note: onEdit/onDelete are kept here in case you want to add 
          // interactive buttons to the calendar list later
          onEdit={
            canEdit
              ? (p) => {
                  setEditingPayment(p);
                  setShowPaymentForm(true);
                }
              : undefined
          }
          onDelete={canEdit ? (p) => handleDeletePayment(p) : undefined}
        />
      )}
      {paymentViewTab === "tracker" && (
        <PaymentTracker
          sales={activeSales}
          clients={clients}
          apartments={apartments}
          payments={payments}
          getPaymentDetails={getPaymentDetails}
          checkOverdue={checkOverduePayments}
          calculateRemaining={calculateRemainingBalance}
        />
      )}
    </div>
  );
}